import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminQueueSummary, fetchAdminReviewQueue } from '../services/api/admin';
import type { AdminDriverListItem, AdminQueueSummary } from '../types/admin';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

function gitStatusLabel(status: string | null | undefined): string {
  if (status === 'VERIFIED') return 'GIT verified';
  if (status === 'PENDING_REVIEW') return 'GIT pending';
  if (status === 'REJECTED') return 'GIT rejected';
  if (status === 'NOT_COVERED') return 'No GIT';
  return 'GIT not declared';
}

function gitStatusPillClass(status: string | null | undefined): string {
  if (status === 'VERIFIED') return 'admin-pill admin-pill-success';
  if (status === 'PENDING_REVIEW') return 'admin-pill admin-pill-warning';
  return 'admin-pill';
}

const AdminReviewQueuePage: React.FC = () => {
  const [summary, setSummary] = useState<AdminQueueSummary | null>(null);
  const [drivers, setDrivers] = useState<AdminDriverListItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    query: '',
    onboardingsStep: 'SUBMITTED',
    onboardingReviewStatus: 'PENDING',
    accountType: '',
    enabled: '',
  });

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [nextSummary, nextQueue] = await Promise.all([
          fetchAdminQueueSummary(),
          fetchAdminReviewQueue({
            ...(filters.onboardingsStep ? { onboardingsStep: filters.onboardingsStep } : {}),
            ...(filters.onboardingReviewStatus
              ? { onboardingReviewStatus: filters.onboardingReviewStatus }
              : {}),
            ...(filters.accountType ? { accountType: filters.accountType } : {}),
            ...(filters.enabled ? { enabled: filters.enabled === 'true' } : {}),
            ...(filters.query ? { query: filters.query.trim() } : {}),
            page,
            size: 10,
          }),
        ]);

        if (!mounted) return;
        setSummary(nextSummary);
        setDrivers(nextQueue.items);
        setTotalPages(nextQueue.totalPages);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load the review queue.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters, page]);

  return (
    <AdminShell
      badge="Driver Review Queue"
      copy="Review new driver submissions, filter the queue, and move quickly into full onboarding review detail."
      title="Submitted drivers queue"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <div className="admin-grid admin-grid-cards" style={{ marginBottom: '18px' }}>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Submitted</span>
          <strong>{summary?.submittedDriversCount ?? 0}</strong>
          <span className="admin-stat-note">Drivers currently in the submitted queue.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Pending payout reviews</span>
          <strong>{summary?.pendingPayoutReviewsCount ?? 0}</strong>
          <span className="admin-stat-note">Related company payout approvals waiting for action.</span>
          <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
            <Link className="register-secondary-button" to="/admin/payout-reviews">
              Open payout reviews
            </Link>
          </div>
        </div>
      </div>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Queue filters</h2>
            <p className="admin-panel-copy">Narrow the onboarding review queue by state, account type, and search terms.</p>
          </div>
        </div>

        <div className="admin-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, query: event.target.value }));
            }}
            placeholder="Search by driver name, email, or phone"
            value={filters.query}
          />
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, onboardingsStep: event.target.value }));
            }}
            value={filters.onboardingsStep}
          >
            <option value="SUBMITTED">Submitted</option>
            <option value="">All steps</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, onboardingReviewStatus: event.target.value }));
            }}
            value={filters.onboardingReviewStatus}
          >
            <option value="PENDING">Pending</option>
            <option value="">All statuses</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, accountType: event.target.value }));
            }}
            value={filters.accountType}
          >
            <option value="">All account types</option>
            <option value="INDIVIDUAL_OWNER">Individual owner</option>
            <option value="COMPANY_DRIVER">Company driver</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, enabled: event.target.value }));
            }}
            value={filters.enabled}
          >
            <option value="">Enabled or disabled</option>
            <option value="true">Enabled only</option>
            <option value="false">Disabled only</option>
          </select>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-table">
              <div className="admin-table-head">
                <span>Driver</span>
                <span>State</span>
                <span>Verification</span>
                <span>Type</span>
                <span>Earnings</span>
                <span>Action</span>
              </div>
              {drivers.map((driver) => (
                <div className="admin-table-row" key={driver.driverId}>
                  <span>
                    <strong>{driver.name}</strong>
                    <small>{driver.email}</small>
                    <small>{driver.phoneE164 || 'No phone supplied'}</small>
                  </span>
                  <span>
                    <strong>{driver.onboardingReviewStatus || 'PENDING'}</strong>
                    <small>{driver.onboardingsStep || 'SUBMITTED'}</small>
                    <small>
                      {driver.onboardingSubmittedAt
                        ? formatDateTime(driver.onboardingSubmittedAt)
                        : 'Not submitted'}
                    </small>
                  </span>
                  <span>
                    <strong>{driver.emailVerified ? 'Email verified' : 'Email pending'}</strong>
                    <small>{driver.phoneVerified ? 'Phone verified' : 'Phone pending'}</small>
                    <small>{driver.enabled ? 'Account enabled' : 'Account disabled'}</small>
                  </span>
                  <span>
                    <strong>{driver.accountType || 'Pending'}</strong>
                    <div className="admin-pill-row" style={{ marginTop: '8px' }}>
                      <span className={gitStatusPillClass(driver.gitStatus)}>{gitStatusLabel(driver.gitStatus)}</span>
                      <span className={driver.riskMode === 'FULL_ACCESS' ? 'admin-pill admin-pill-success' : 'admin-pill admin-pill-warning'}>
                        {driver.riskMode === 'FULL_ACCESS' ? 'Full access' : 'Restricted'}
                      </span>
                    </div>
                  </span>
                  <span>
                    <strong>{formatCurrency(driver.totalEarnings)}</strong>
                    <small>Week {formatCurrency(driver.weeklyEarnings)}</small>
                  </span>
                  <span className="admin-table-actions">
                    <Link className="register-submit register-submit-inline" to={`/admin/drivers/${driver.driverId}`}>
                      Review
                    </Link>
                  </span>
                </div>
              ))}
              {!drivers.length ? <p className="admin-empty">No drivers match this queue view right now.</p> : null}
            </div>

            <div className="admin-pagination">
              <button
                className="register-secondary-button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                type="button"
              >
                Previous
              </button>
              <span>
                Page {page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button
                className="register-secondary-button"
                disabled={page + 1 >= Math.max(totalPages, 1)}
                onClick={() => setPage((current) => current + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
    </AdminShell>
  );
};

export default AdminReviewQueuePage;
